import { createAction, createReducer, createSelector } from '@reduxjs/toolkit';
import { _take } from '../../listenerMiddleware';
import Notifications from 'react-notification-system-redux';
import { NodeLabel, NodeLabelSchema } from '../../../general/node';
import { api } from '../../../general/apiScenarios';
import { scenarioFactoryAsync } from '../../../general/scenarioFactoryAsync';
import { z } from 'zod';
import { ListenerApiSafe } from '../../../general/listenerMiddlewareAddons';
import { createListenerMiddleware } from '@myua/rtk-helper';
import type { THelpers } from '@myua/rtk-helper';

export const FigmaImportStatus = z.union([
  z.literal('pending'),
  z.object({ node_id: z.number() }),
  z.object({ error: z.string() }),
]);

export const FigmaImportJobSchema = z.object({
  originalNode: NodeLabelSchema,
  status: FigmaImportStatus,
});
export const FigmaImportShape = z.record(z.string(), FigmaImportJobSchema);
// eslint-disable-next-line @typescript-eslint/no-redeclare
export type FigmaImportShape = z.infer<typeof FigmaImportShape>;
const StoreSlice = z.object({
  figmaImport: FigmaImportShape,
});
// eslint-disable-next-line @typescript-eslint/no-redeclare
export type StoreSlice = z.infer<typeof StoreSlice>;

export type ValueType<X> =
  X extends Map<any, infer V> ? V : X extends Record<any, infer V> ? V : never;

export const importRequested = createAction<NodeLabel[], 'general/figmaImport/IMPORT_REQUESTED'>(
  'general/figmaImport/IMPORT_REQUESTED',
);
export const taskSubmitted = createAction<
  { node: NodeLabel; taskID: string },
  'general/figmaImport/TASK_SUBMITTED'
>('general/figmaImport/TASK_SUBMITTED');
export const figmaImportTaskUpdated = createAction<
  {
    taskID: string;
    status: ValueType<FigmaImportShape>['status'];
    originalNode: z.infer<typeof NodeLabelSchema>;
  },
  'general/figmaImport/TASK_UPDATED'
>('general/figmaImport/TASK_UPDATED');
export const taskDeleted = createAction<{ taskID: string }, 'general/figmaImport/TASK_DELETED'>(
  'general/figmaImport/TASK_DELETED',
);
export const taskDeletedAll = createAction<void, 'general/figmaImport/TASK_DELETED_ALL'>(
  'general/figmaImport/TASK_DELETED_ALL',
);
export const btnCancelClicked = createAction<void, 'general/figmaImport/CANCEL'>(
  'general/figmaImport/CANCEL',
);

export const figmaImportReducer = createReducer<FigmaImportShape>({}, (b) =>
  b

    .addCase(taskSubmitted, (s, a) => {
      s[a.payload.taskID] = { originalNode: a.payload.node, status: 'pending' };
    })

    .addCase(figmaImportTaskUpdated, (s, a) => {
      const value = s[a.payload.taskID];
      if (value) {
        s[a.payload.taskID] = { ...value, status: a.payload.status };
      }
    })
    .addCase(taskDeleted, (s, a) => {
      delete s[a.payload.taskID];
    })
    .addCase(taskDeletedAll, () => {
      return {};
    }),
);

export const getFigmaImport = createSelector(
  (x: any): StoreSlice => StoreSlice.parse(x),
  (state) => state.figmaImport,
);
export const getFigmaImportJobs = createSelector(getFigmaImport, (state) => Object.values(state));
export const getNodeList = createSelector(getFigmaImportJobs, (state) =>
  state.map((x) => x.originalNode),
);

export type ListenerMiddlewareFigmaInstance = ReturnType<
  typeof createListenerMiddleware<StoreSlice>
>;

export function figmaImportRootListener(lm: THelpers<StoreSlice>) {
  console.debug('figmaImportRootListener');
  const scenario = scenarioFactoryAsync(
    async (lapi: ListenerApiSafe<StoreSlice>, nodes: NodeLabel[]) => {
      await Promise.all(
        nodes.map(async (node) => {
          const result = await api.figma.request(node.id);
          if (!result.success) {
            lapi.dispatch(
              Notifications.error({
                title: 'Failed to submit task for node ' + node.name,
                message: JSON.stringify(result.errorData?.response),
              }),
            );
            return;
          }
          const taskID = result.data.response.task_id;
          lapi.dispatch(taskSubmitted({ node, taskID }));

          try {
            const taskResult = await api.core.task<{ node_id: number }>(taskID, lapi.signal);
            lapi.signal.throwIfAborted();
            lapi.dispatch(
              figmaImportTaskUpdated({
                taskID,
                status: taskResult,
                originalNode: node,
              }),
            );
            // if task succeeded
          } catch (e) {
            lapi.dispatch(
              figmaImportTaskUpdated({
                taskID,
                status: { error: e as string },
                originalNode: node,
              }),
            );
            // if task failed
          }
        }),
      );
    },
    async (lapi: ListenerApiSafe<StoreSlice>) => {
      await _take(lapi, btnCancelClicked);
      lapi.dispatch(taskDeletedAll()); // reset creativeUploader
    },
    () => {},
    'Figma Import',
  );

  lm.takeEvery(importRequested, async (action, lapi) => {
    console.debug('figmaImportRootListener - importRequested');
    await scenario(lapi, action.payload);
  });
}
